// src/components/DoctorOnboarding.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../layout/index";
import { APP_ROUTES } from "../../constants/approutes";
import { toast } from "react-toastify";
import { DatePicker, TimePicker } from "antd";
import FullScreenLoader from "../FullScreenLoader";
import dayjs from "dayjs";

const hasOrganizationOnboarded = () => {
  if (localStorage.getItem("organizationOnboarded") === "true") {
    return true;
  }
  const clinicId = localStorage.getItem("clinicId") || "";
  const clinicName = localStorage.getItem("clinicname") || "";
  const hospitalId = localStorage.getItem("hospitalid") || "";
  const categoryType = localStorage.getItem("categoryType");

  if (categoryType === "HOSPI360") {
    return Boolean(hospitalId.trim() || clinicId.trim() || clinicName.trim());
  }
  return Boolean(clinicId.trim() || clinicName.trim());
};

function DoctorOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const parentCode = location.state?.parentCode;
  const API_URL = process.env.REACT_APP_API_URL;
  const clinicId = localStorage.getItem("clinicId");
  console.log(clinicId, "clinicId");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // Track individual field errors
  const [isWeeksOpen, setIsWeeksOpen] = useState(false);
  const [isDaysOpen, setIsDaysOpen] = useState(false);

  const [formData, setFormData] = useState({
    doctorName: "",
    licenseNumber: "",
    qualifications: "",
    specialization: [],
    experience: "",
    phone: "",
    email1: "",
    isFamilyDoctor: "",
    fromDate: dayjs().format("YYYY-MM-DD"), // Initialize with current date
    toDate: dayjs().format("YYYY-MM-DD"),   // Initialize with current date
    timeSlots: [],
    dayOfWeek: "",
    bio: "",
    slotDuration: "",
    // Bank & account fields
    bankName: "",
    accountHolderName: "",
    ifscCode: "",
    accountNumber: "",
    bankLoc: "",
    // PAN / Aadhaar / Signature / Stamp / Profile
    panCardNumber: "",
    panPhoto: null,
    adhharCardNo: "",
    adharPhoto: null,
    signaturePhoto: null,
    stampPhoto: null,
    profilePhoto: null,
  });

  // New state for specialization search and options
  const [specializationSearch, setSpecializationSearch] = useState("");
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [loadingSpecializations, setLoadingSpecializations] = useState(false);
  const [showSpecializationDropdown, setShowSpecializationDropdown] =
    useState(false);

  // Fetch specializations from API
  const fetchSpecializations = async (searchTerm = "") => {
    try {
      setLoadingSpecializations(true);
      const url = searchTerm
        ? `${API_URL}api/v1/speciality?search=${encodeURIComponent(searchTerm)}`
        : `${API_URL}api/v1/speciality`;

      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSpecializationOptions(data || []);
      } else {
        console.error("Failed to fetch specializations");
        setSpecializationOptions([]);
      }
    } catch (error) {
      console.error("Error fetching specializations:", error);
      setSpecializationOptions([]);
    } finally {
      setLoadingSpecializations(false);
    }
  };

  // Load specializations on component mount
  useEffect(() => {
    fetchSpecializations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest(".specialization-dropdown-container")) {
        setShowSpecializationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close weeks and days dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest(".weeks-dropdown-container")) {
        setIsWeeksOpen(false);
      }
      if (!target.closest(".days-dropdown-container")) {
        setIsDaysOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debounced search for specializations
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (specializationSearch.trim()) {
        fetchSpecializations(specializationSearch);
      } else {
        fetchSpecializations();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [specializationSearch]);

  // Handle specialization selection
  const handleSpecializationSelect = (specialization) => {
    if (!formData.specialization.includes(specialization.speciality)) {
      setFormData((prev) => ({
        ...prev,
        specialization: [...prev.specialization, specialization.speciality],
      }));
      // Clear error when field is filled
      setFieldErrors(prev => ({ ...prev, specialization: null }));
    }
    setSpecializationSearch("");
    setShowSpecializationDropdown(false);
  };

  // Handle specialization removal
  const handleSpecializationRemove = (specializationToRemove) => {
    const updatedSpecializations = formData.specialization.filter(
      (spec) => spec !== specializationToRemove
    );
    setFormData((prev) => ({
      ...prev,
      specialization: updatedSpecializations,
    }));
    // Add error if no specializations left
    if (updatedSpecializations.length === 0) {
      setFieldErrors(prev => ({ ...prev, specialization: "At least one specialization is required" }));
    }
  };

  // New state for consultation fees
  const [consultationFeeEntry, setConsultationFeeEntry] = useState({
    consultationtype: "",
    consultationFee: "",
  });
  const [consultationFees, setConsultationFees] = useState([]);

  const [currentSlot, setCurrentSlot] = useState({
    dayOfWeek: [],
    startTime: "",
    endTime: "",
    weekNumbers: [],
  });

  // Validate individual field and update errors
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'doctorName':
        if (!value.trim()) error = "Doctor name is required";
        break;
      case 'licenseNumber':
        // Medical license number is now optional
        break;
      case 'qualifications':
        if (!value.trim()) error = "Qualifications are required";
        break;
      case 'specialization':
        if (!Array.isArray(value) || value.length === 0) error = "At least one specialization is required";
        break;
      case 'isFamilyDoctor':
        if (value !== "true" && value !== "false") error = "Please select whether this is a family doctor";
        break;
      case 'experience':
        if (!value.trim()) error = "Experience is required";
        break;
      case 'phone':
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (value.trim().length !== 10) {
          error = "Phone number must be exactly 10 digits";
        }
        break;
      case 'email1':
        if (!value.trim()) error = "Email is required";
        break;
      case 'fromDate':
        // From date is now optional
        break;
      case 'toDate':
        // To date is now optional
        break;
      case 'timeSlots':
        if (!Array.isArray(value) || value.length === 0) error = "At least one time slot is required";
        break;
      case 'bio':
        if (!value.trim()) error = "Doctor bio is required";
        break;
      case 'bankName':
        if (!value.trim()) error = "Bank name is required";
        break;
      case 'bankLoc':
        if (!value.trim()) error = "Branch location is required";
        break;
      case 'accountHolderName':
        if (!value.trim()) error = "Account holder name is required";
        break;
      case 'ifscCode':
        if (!value.trim()) error = "IFSC code is required";
        break;
      case 'accountNumber':
        if (!value.trim()) error = "Account number is required";
        break;
      case 'panCardNumber':
        // PAN card number is now optional
        if (value.trim() && value.trim().length !== 10) {
          error = "PAN card number must be exactly 10 characters";
        }
        break;
      case 'panPhoto':
        // PAN photo is now optional
        break;
      case 'adhharCardNo':
        // Aadhaar number is now optional
        if (value.trim() && value.trim().length !== 12) {
          error = "Aadhaar number must be exactly 12 digits";
        }
        break;
      case 'adharPhoto':
        // Aadhaar photo is now optional
        break;
      case 'signaturePhoto':
        // Signature photo is now optional
        break;
      case 'stampPhoto':
        // Stamp photo is now optional
        break;
      case 'profilePhoto':
        // Profile photo is now optional
        break;
      case 'consultationFees':
        if (!Array.isArray(value) || value.length === 0) error = "At least one consultation fee is required";
        break;
        case 'slotDuration':
          // Slot duration is now optional, but must be valid if provided
          if (value.trim()) {
            if (!/^\d+$/.test(value)) {
              error = "Slot duration must be a number";
            } else if (parseInt(value, 10) <= 0) {
              error = "Slot duration must be greater than 0";
            }
          }
          break;
    }

    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    let newValue = value;

    if (type === "file") {
      newValue = files[0] || null;
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      validateField(name, newValue);
      return;
    }

    // Handle date fields - keep as string format
    if (name === "fromDate" || name === "toDate") {
      newValue = value; // Already in YYYY-MM-DD format from DatePicker
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      validateField(name, newValue);
      return;
    }

    // Handle boolean-select fields
    if (name === "isFamilyDoctor") {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      validateField(name, newValue);
      return;
    }

    // Field-specific sanitization/validation
    if (name === "phone") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "adhharCardNo") {
      newValue = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "accountHolderName") {
      newValue = value.replace(/[^a-zA-Z ]/g, "");
    } else if (name === "accountNumber") {
      newValue = value.replace(/\D/g, "");
    }
    else if (name === "doctorName") {
      newValue = value.replace(/[^a-zA-Z ]/g, "");
    } else if (name === "bankName") {
      newValue = value.replace(/[^a-zA-Z ]/g, "");
    } 

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    validateField(name, newValue);
  };

  const isFormValid = () => {
    return (
      formData.doctorName.trim() &&
      formData.qualifications.trim() &&
      formData.specialization.length > 0 &&
      formData.experience.trim() &&
      formData.phone.trim().length === 10 &&
      formData.email1.trim() &&
      (formData.isFamilyDoctor === "true" || formData.isFamilyDoctor === "false") &&
      formData.bio.trim() &&
      consultationFees.length > 0 &&
      //–– make sure all bank fields are filled:
      formData.bankName.trim() &&
      formData.bankLoc.trim() &&
      formData.accountHolderName.trim() &&
      formData.ifscCode.trim() &&
      formData.accountNumber.trim()
      //–– document fields are now optional, so we don't check them here
    );
  };

  // Validate all fields and show errors
  const validateAllFields = () => {
    const errors = {};
    
    // Validate all required fields (documents are now optional)
    const fieldsToValidate = [
      'doctorName', 'qualifications', 'specialization',
      'experience', 'phone', 'email1', 'isFamilyDoctor',
      'bio',
      //  'bankName', 'bankLoc', 'accountHolderName', 'ifscCode',
      // 'accountNumber',
      // Document fields are now optional - only validate if they have values
      'panCardNumber', 'adhharCardNo'
    ];

    // Validate document/optional fields only if they have values
    if (formData.panCardNumber.trim()) {
      fieldsToValidate.push('panCardNumber');
    }
    if (formData.adhharCardNo.trim()) {
      fieldsToValidate.push('adhharCardNo');
    }
    if (formData.slotDuration.trim()) {
      fieldsToValidate.push('slotDuration');
    }

    fieldsToValidate.forEach(fieldName => {
      const fieldValue = formData[fieldName];
      const error = validateField(fieldName, fieldValue);
      if (error) {
        errors[fieldName] = error;
      }
    });

    // Validate consultation fees separately
    if (consultationFees.length === 0) {
      errors.consultationFees = "At least one consultation fee is required";
      setFieldErrors(prev => ({ ...prev, consultationFees: "At least one consultation fee is required" }));
    }

    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    const tenantId = localStorage.getItem("tenantId");
    const clinicId = localStorage.getItem("clinicId");
    const categoryType = localStorage.getItem("categoryType");
    if (!hasOrganizationOnboarded()) {
      toast.error(
        "Please onboard organization information first before onboarding a doctor."
      );
      return;
    }

    if (!validateAllFields()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }
    
    setIsLoggingIn(true);

    const qualificationArray = Array.isArray(formData.qualifications)
      ? formData.qualifications
      : formData.qualifications
          .split(",")
          .map((q) => q.trim())
          .filter((q) => q.length > 0);

    const doctorPayload = {
      fullName: formData.doctorName,
     ...(formData.licenseNumber.trim() && { medicalLicenseNumber: formData.licenseNumber }),
      qualifications: qualificationArray, // now definitely an array
      specialization: formData.specialization, // now an array
      yearsOfExperience: parseInt(formData.experience, 10),
      bio: formData.bio,
      slotDuration: formData.slotDuration,
      phoneNumber: formData.phone,
      email: formData.email1,
      isFamilyDoctor: formData.isFamilyDoctor === "true",
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      timeSlots: formData.timeSlots,
      doctorFee: consultationFees,
      clinicId: tenantId,
      bankName: formData.bankName,
      accountHolderName: formData.accountHolderName,
      ifscCode: formData.ifscCode,
      accountNumber: formData.accountNumber,
      panCardNumber: formData.panCardNumber,
      adharCardNo: formData.adhharCardNo,
      fcmToken: "",
      bioMetricID: "",
      bankLoc: formData.bankLoc,
      tenantId:tenantId,
      categoryType:categoryType
    };

    const payload = new FormData();
    payload.append("doctor", JSON.stringify(doctorPayload));
    if (formData.panPhoto) {
      payload.append("panPhoto", formData.panPhoto);
    }
    if (formData.adharPhoto) {
      payload.append("adharPhoto", formData.adharPhoto);
    }
    if (formData.signaturePhoto) {
      payload.append("signaturePhoto", formData.signaturePhoto);
    }
    if (formData.stampPhoto) {
      payload.append("stampPhoto", formData.stampPhoto);
    }
    if (formData.profilePhoto) {
      payload.append("profilePhoto", formData.profilePhoto);
    }

    console.log("Doctor Onboarding Payload:", doctorPayload);

    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}doctor/onboard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // tenantId: tenantId,
        },
        body: payload,
      });

      const result = await res.json();
    //  setIsLoggingIn(false);
      console.log(result.error,"dcfvgbhnjk")

      if (result.status === "SUCCESS") {
        // toast.success("Doctor onboarded successfully!");
        handleSubmitTenant(result.doctorCode);
      } else if (result.error) {
        setIsLoggingIn(false);
        toast.error(result.error);
      }
      else {
        toast.error(result || "Onboarding failed.");
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error("Something went wrong while onboarding.");
      setIsLoggingIn(false);
    }
    
  };

  const handleSubmitTenant = async (doctorCode) => {
       const tenantId = localStorage.getItem("tenantId");
        const clinicId = localStorage.getItem("clinicId");
        const categoryType = localStorage.getItem("categoryType");
     setIsLoggingIn(true);

    const qualificationArray = Array.isArray(formData.qualifications)
      ? formData.qualifications
      : formData.qualifications
          .split(",")
          .map((q) => q.trim())
          .filter((q) => q.length > 0);

    const doctorPayload = {
      fullName: formData.doctorName,
      ...(formData.licenseNumber.trim() && { medicalLicenseNumber: formData.licenseNumber }),
      qualifications: qualificationArray, // now definitely an array
      specialization: formData.specialization, // now an array
      yearsOfExperience: parseInt(formData.experience, 10),
      bio: formData.bio,
      slotDuration: formData.slotDuration,
      phoneNumber: formData.phone,
      email: formData.email1,
      isFamilyDoctor: formData.isFamilyDoctor === "true",
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      timeSlots: formData.timeSlots,
      doctorFee: consultationFees,
      clinicId: tenantId,
      bankName: formData.bankName,
      accountHolderName: formData.accountHolderName,
      ifscCode: formData.ifscCode,
      accountNumber: formData.accountNumber,
      panCardNumber: formData.panCardNumber,
      adharCardNo: formData.adhharCardNo,
      fcmToken: "",
      bioMetricID: "",
      bankLoc: formData.bankLoc,
      tenantId:tenantId,
      doctorCode:doctorCode,
      categoryType:categoryType
    };

    const payload = new FormData();
    payload.append("doctor", JSON.stringify(doctorPayload));
    if (formData.panPhoto) {
      payload.append("panPhoto", formData.panPhoto);
    }
    if (formData.adharPhoto) {
      payload.append("adharPhoto", formData.adharPhoto);
    }
    if (formData.signaturePhoto) {
      payload.append("signaturePhoto", formData.signaturePhoto);
    }
    if (formData.stampPhoto) {
      payload.append("stampPhoto", formData.stampPhoto);
    }
    if (formData.profilePhoto) {
      payload.append("profilePhoto", formData.profilePhoto);
    }


    const token = localStorage.getItem("auth_token");
 
    try {
      const res = await fetch(`${API_URL}doctor/onboard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
           tenantId: tenantId,
        },
        body: payload,
      });

      const result = await res.json();
       setIsLoggingIn(false);
     if (res.ok) {
        toast.success("Doctor onboarded successfully!");
        navigate("/DoctorList");
      } else if (result.error) {
        toast.error(result.error);
      }
      else {
        toast.error(result.message || "Onboarding failed.");
      }

     
    } catch (error) {
      console.error("API error:", error);
       setIsLoggingIn(false);
     
    }
  };

  return (
    <>
      {isLoggingIn && <FullScreenLoader message="" />}

      <DashboardLayout>
        <div className="min-h-screen bg-white p-4 md:pt-10">
          <h2 className="text-lg font-semibold text-[#004276] md:text-xl">
            Doctor Onboard
          </h2>

          {!hasOrganizationOnboarded() && (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
              <p className="text-sm font-medium">
                Organization information is not onboarded yet. Please complete
                organization onboarding before onboarding a doctor.
              </p>
              <button
                type="button"
                onClick={() => navigate(APP_ROUTES.CLINIC_ONBOARDING)}
                className="mt-2 text-sm font-semibold text-[#004276] underline hover:text-[#00BD7F]"
              >
                Go to Organization Onboarding
              </button>
            </div>
          )}

          <div className="mt-8 space-y-8">
            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* BASIC DOCTOR INFO */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <InputField
                  label="Doctor Name"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleChange}
                  error={fieldErrors.doctorName}
                />
                <InputField
                  label="Medical License Number"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  error={fieldErrors.licenseNumber}
                  showRequired={false}
                />
                <InputField
                  label="Qualifications"
                  name="qualifications"
                  value={formData.qualifications}
                  onChange={handleChange}
                  error={fieldErrors.qualifications}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Specialization <span className="text-red-500">*</span>
                  </label>
                  <div className="relative specialization-dropdown-container">
                    <input
                      type="text"
                      placeholder="Search or select specializations..."
                      value={specializationSearch}
                      onChange={(e) => setSpecializationSearch(e.target.value)}
                      onFocus={() => setShowSpecializationDropdown(true)}
                      className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
                        fieldErrors.specialization ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowSpecializationDropdown(
                          !showSpecializationDropdown
                        )
                      }
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          showSpecializationDropdown ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {loadingSpecializations && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#3cba84]"></div>
                      </div>
                    )}

                    {/* Dropdown for specialization options */}
                    {showSpecializationDropdown &&
                      specializationOptions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {specializationOptions
                            .filter((option) => option.status === 1) // Only show active specializations
                            .filter(
                              (option) =>
                                !specializationSearch ||
                                option.speciality
                                  .toLowerCase()
                                  .includes(specializationSearch.toLowerCase())
                            ) // Filter by search term if provided
                            .map((option, index) => (
                              <button
                                key={option.id || index}
                                type="button"
                                onClick={() =>
                                  handleSpecializationSelect(option)
                                }
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-gray-700"
                              >
                                {option.speciality}
                              </button>
                            ))}
                        </div>
                      )}
                  </div>

                  {/* Selected specializations */}
                  {formData.specialization.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.specialization.map((spec, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-[#3cba84] text-white px-3 py-1 rounded-full text-xs"
                        >
                          {spec}
                          <button
                            type="button"
                            onClick={() => handleSpecializationRemove(spec)}
                            className="ml-1 hover:bg-[#2da06f] rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Error message for specialization */}
                  {fieldErrors.specialization && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.specialization}</p>
                  )}
                </div>
                <InputField
                  label="Experience (Years)"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  error={fieldErrors.experience}
                />
                <InputField
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  error={fieldErrors.phone}
                />
                <InputField
                  label="Email Id"
                  name="email1"
                  type="email"
                  value={formData.email1}
                  onChange={handleChange}
                  error={fieldErrors.email1}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Family Doctor <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="isFamilyDoctor"
                    value={formData.isFamilyDoctor}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
                      fieldErrors.isFamilyDoctor ? 'border-red-500' : 'border-gray-200'
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  {fieldErrors.isFamilyDoctor && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.isFamilyDoctor}</p>
                  )}
                </div>
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* REQUIRED DOCUMENTS */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
              Required Documents
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    PAN Card Number
                  </label>
                  <input
                    type="text"
                    name="panCardNumber"
                    value={formData.panCardNumber}
                    onChange={handleChange}
                    placeholder="Enter here"
                    className={`rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
                      fieldErrors.panCardNumber ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  {fieldErrors.panCardNumber && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.panCardNumber}</p>
                  )}
                </div>

                <FileInputField
                  label="PAN Photo (Image/PDF)"
                  name="panPhoto"
                  file={formData.panPhoto}
                  onChange={handleChange}
                  error={fieldErrors.panPhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, panPhoto: null }))}
                />

                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Aadhaar Number
                  </label>
                  <input
                    type="text"
                    name="adhharCardNo"
                    value={formData.adhharCardNo}
                    onChange={handleChange}
                    placeholder="Enter here"
                    className={`rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
                      fieldErrors.adhharCardNo ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  {fieldErrors.adhharCardNo && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.adhharCardNo}</p>
                  )}
                </div>

                <FileInputField
                  label="Aadhaar Photo (Image/PDF)"
                  name="adharPhoto"
                  file={formData.adharPhoto}
                  onChange={handleChange}
                  error={fieldErrors.adharPhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, adharPhoto: null }))}
                />

                <FileInputField
                  label="Signature Photo"
                  name="signaturePhoto"
                  file={formData.signaturePhoto}
                  onChange={handleChange}
                  error={fieldErrors.signaturePhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, signaturePhoto: null }))}
                />

                <FileInputField
                  label="Stamp Photo"
                  name="stampPhoto"
                  file={formData.stampPhoto}
                  onChange={handleChange}
                  error={fieldErrors.stampPhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, stampPhoto: null }))}
                />

                <FileInputField
                  label="Profile Photo"
                  name="profilePhoto"
                  file={formData.profilePhoto}
                  onChange={handleChange}
                  error={fieldErrors.profilePhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, profilePhoto: null }))}
                />
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* BANK DETAILS */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Bank Details
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <InputField
                  label="Bank Name"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  error={fieldErrors.bankName}
                  showRequired={false}
                />
                <InputField
                  label="Branch Location"
                  name="bankLoc"
                  value={formData.bankLoc}
                  onChange={handleChange}
                  error={fieldErrors.bankLoc}
                  showRequired={false}
                />
                <InputField
                  label="Account Holder Name"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  error={fieldErrors.accountHolderName}
                  showRequired={false}
                />
                <InputField
                  label="IFSC Code"
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleChange}
                  error={fieldErrors.ifscCode}
                  showRequired={false}
                />
                <InputField
                  label="Account Number"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  error={fieldErrors.accountNumber}
                  showRequired={false}
                />
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* CONSULTATION FEES */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Consultation Fees <span className="text-red-500">*</span>
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Consultation Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="consultationtype"
                    value={consultationFeeEntry.consultationtype}
                    onChange={(e) =>
                      setConsultationFeeEntry((prev) => ({
                        ...prev,
                        consultationtype: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
                  >
                    <option value="">Select</option>
                    <option value="V">Video</option>
                    <option value="I">In-Person</option>
                      <option value="S">Surgery</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Fee (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    name="consultationFee"
                    value={consultationFeeEntry.consultationFee}
                    onChange={(e) =>
                      setConsultationFeeEntry((prev) => ({
                        ...prev,
                        consultationFee: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
                    placeholder="Enter fee"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <button
                    type="button"
                    className="w-full bg-[#004276] hover:bg-[#003a5c] text-white py-2 px-6 rounded-lg transition"
                    onClick={() => {
                      if (
                        consultationFeeEntry.consultationtype &&
                        consultationFeeEntry.consultationFee &&
                        !consultationFees.some(
                          (f) =>
                            f.consultationtype ===
                            consultationFeeEntry.consultationtype
                        )
                      ) {
                        setConsultationFees((prev) => [
                          ...prev,
                          {
                            consultationtype:
                              consultationFeeEntry.consultationtype,
                            consultationFee: Number(
                              consultationFeeEntry.consultationFee
                            ),
                          },
                        ]);
                        setConsultationFeeEntry({
                          consultationtype: "",
                          consultationFee: "",
                        });
                        // Clear error when fee is added
                        setFieldErrors(prev => ({ ...prev, consultationFees: null }));
                      } else if (
                        consultationFees.some(
                          (f) =>
                            f.consultationtype ===
                            consultationFeeEntry.consultationtype
                        )
                      ) {
                        toast.error("Fee for this type already added.");
                      } else {
                        toast.error("Please select type and enter fee.");
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* List of added fees */}
              {consultationFees.length > 0 && (
                <div className="mt-4 space-y-2">
                  {consultationFees.map((fee, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-lg"
                    >
                      <span className="text-sm text-gray-700">
                        {fee.consultationtype === "V" ? "Video" :fee.consultationtype === "I" ? "In-Person" : "Surgery"}{" "}
                        Consultation: ₹{fee.consultationFee}
                      </span>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 font-bold ml-4 transition"
                        onClick={() => {
                          const updatedFees = consultationFees.filter((_, i) => i !== idx);
                          setConsultationFees(updatedFees);
                          // Add error if no fees left
                          if (updatedFees.length === 0) {
                            setFieldErrors(prev => ({ ...prev, consultationFees: "At least one consultation fee is required" }));
                          }
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Error message for consultation fees */}
              {fieldErrors.consultationFees && (
                <p className="text-red-500 text-xs mt-2">{fieldErrors.consultationFees}</p>
              )}
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* DOCTOR AVAILABILITY */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Doctor Availability
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
                <div className="flex flex-col">
                  <DatePickerField
                    label="From Date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    error={fieldErrors.fromDate}
                  />
                </div>
                <div className="flex flex-col">
                  <DatePickerField
                    label="To Date"
                    name="toDate"
                    value={formData.toDate}
                    onChange={handleChange}
                    error={fieldErrors.toDate}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Weeks
                  </label>
                  <div className="relative weeks-dropdown-container">
                    <div
                      onClick={() => setIsWeeksOpen(!isWeeksOpen)}
                      className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition cursor-pointer bg-white"
                    >
                      {currentSlot.weekNumbers.length > 0 
                        ? `Selected: ${currentSlot.weekNumbers.join(", ")}`
                        : "Select Week"
                      }
                    </div>
                    
                    {isWeeksOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {[1, 2, 3, 4, 5].map((week) => (
                          <div
                            key={week}
                            onClick={() => {
                              const alreadySelected = currentSlot.weekNumbers.includes(week);
                              setCurrentSlot((prev) => ({
                                ...prev,
                                weekNumbers: alreadySelected
                                  ? prev.weekNumbers.filter((w) => w !== week)
                                  : [...prev.weekNumbers, week],
                              }));
                            }}
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                              currentSlot.weekNumbers.includes(week)
                                ? "bg-blue-50 border-r-4 border-blue-500"
                                : ""
                            }`}
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border-2 mr-3 flex items-center justify-center ${
                                  currentSlot.weekNumbers.includes(week)
                                    ? "bg-blue-500 border-blue-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {currentSlot.weekNumbers.includes(week) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={currentSlot.weekNumbers.includes(week) ? "text-blue-700 font-medium" : "text-gray-700"}>
                                Week {week}
                              </span>
                            </div>
                            {currentSlot.weekNumbers.includes(week) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentSlot((prev) => ({
                                    ...prev,
                                    weekNumbers: prev.weekNumbers.filter((w) => w !== week),
                                  }));
                                }}
                                className="w-5 h-5 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* <div className="flex flex-col">
                  <SelectField
                    label="Day"
                    name="dayOfWeek"
                    value={currentSlot.dayOfWeek}
                    onChange={(e) =>
                      setCurrentSlot((prev) => ({
                        ...prev,
                        dayOfWeek: e.target.value,
                      }))
                    }
                    options={[
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday",
                    ]}
                  />
                </div> */}
                <div className="flex flex-col">                   
                  <label className="text-sm text-[#004276] mb-1 font-medium">                     
                    Days                   
                  </label>
                  <div className="relative days-dropdown-container">
                    <div
                      onClick={() => setIsDaysOpen(!isDaysOpen)}
                      className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition cursor-pointer bg-white"
                    >
                      {currentSlot.dayOfWeek.length > 0 
                        ? `Selected: ${currentSlot.dayOfWeek.join(", ")}`
                        : "Select Day"
                      }
                    </div>
                    
                    {isDaysOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {[
                          "Monday",
                          "Tuesday", 
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday", 
                          "Sunday"
                        ].map((day) => (
                          <div
                            key={day}
                            onClick={() => {
                              const alreadySelected = currentSlot.dayOfWeek.includes(day);
                              setCurrentSlot((prev) => ({
                                ...prev,
                                dayOfWeek: alreadySelected
                                  ? prev.dayOfWeek.filter((d) => d !== day)
                                  : [...prev.dayOfWeek, day],
                              }));
                            }}
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                              currentSlot.dayOfWeek.includes(day)
                                ? "bg-blue-50 border-r-4 border-blue-500"
                                : ""
                            }`}
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border-2 mr-3 flex items-center justify-center ${
                                  currentSlot.dayOfWeek.includes(day)
                                    ? "bg-blue-500 border-blue-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {currentSlot.dayOfWeek.includes(day) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={currentSlot.dayOfWeek.includes(day) ? "text-blue-700 font-medium" : "text-gray-700"}>
                                {day}
                              </span>
                            </div>
                            {currentSlot.dayOfWeek.includes(day) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentSlot((prev) => ({
                                    ...prev,
                                    dayOfWeek: prev.dayOfWeek.filter((d) => d !== day),
                                  }));
                                }}
                                className="w-5 h-5 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Start Time
                  </label>
                  <TimePicker
                    value={
                      currentSlot.startTime
                        ? dayjs(currentSlot.startTime, "HH:mm")
                        : null
                    }
                    onChange={(time, timeString) =>
                      setCurrentSlot((prev) => ({
                        ...prev,
                        startTime: timeString,
                      }))
                    }
                    format="HH:mm"
                    className="w-full"
                    minuteStep={5}
                    use12Hours={false}
                    placeholder="Select time"
                    needConfirm={false}
                    showNow={false}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    End Time
                  </label>
                  <TimePicker
                    value={
                      currentSlot.endTime
                        ? dayjs(currentSlot.endTime, "HH:mm")
                        : null
                    }
                    onChange={(time, timeString) =>
                      setCurrentSlot((prev) => ({
                        ...prev,
                        endTime: timeString,
                      }))
                    }
                    format="HH:mm"
                    className="w-full"
                    minuteStep={5}
                    use12Hours={false}
                    placeholder="Select time"
                    needConfirm={false}
                    showNow={false}
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        currentSlot.dayOfWeek.length > 0 &&
                        currentSlot.startTime &&
                        currentSlot.endTime
                      ) {
                        const newSlots = currentSlot.dayOfWeek.map(day => {
                          const newSlot = {
                            dayOfWeek: day, 
                            startTime: currentSlot.startTime,
                            endTime: currentSlot.endTime,
                          };

                          if (currentSlot.weekNumbers.length > 0) {
                            newSlot.weekNumbers = currentSlot.weekNumbers;
                          }

                          return newSlot;
                        });

                        setFormData((prev) => ({
                          ...prev,
                          timeSlots: [...prev.timeSlots, ...newSlots],
                        }));

                        setFieldErrors(prev => ({ ...prev, timeSlots: null }));

                        setCurrentSlot({
                          dayOfWeek: [], 
                          startTime: "",
                          endTime: "",
                          weekNumbers: [],
                        });
                      } else {
                        toast.error(
                          "Please fill all time-slot fields (Week is optional)"
                        );
                      }
                    }}
                    className="w-full bg-[#004276] hover:bg-[#003a5c] text-white py-2 rounded-lg transition"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Time Slot Summary */}
              {formData.timeSlots.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {formData.timeSlots.map((slot, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center bg-gray-100 px-4 py-2 rounded-lg"
                      >
                        <div className="text-sm text-gray-700">
                          {slot.dayOfWeek} – {slot.startTime} to {slot.endTime}
                          {slot.weekNumbers?.length > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              (Weeks: {slot.weekNumbers.join(", ")})
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formData.timeSlots.filter(
                              (_, i) => i !== index
                            );
                            setFormData((prev) => ({
                              ...prev,
                              timeSlots: updated,
                            }));
                            // Add error if no time slots left
                            if (updated.length === 0) {
                              setFieldErrors(prev => ({ ...prev, timeSlots: "At least one time slot is required" }));
                            }
                          }}
                          className="text-red-500 hover:text-red-700 font-bold transition"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
              )}
              
              {/* Error message for time slots */}
              {fieldErrors.timeSlots && (
                <p className="text-red-500 text-xs mt-2">{fieldErrors.timeSlots}</p>
              )}



              <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-[#004276] mt-4">
                Slot Duration
              </h3>
                <InputField
                  label="Duration (minutes)"
                  name="slotDuration"
                  value={formData.slotDuration}
                  onChange={handleChange}
                  error={fieldErrors.slotDuration}
                  type="number"
                  min="1"
                  placeholder="Enter duration in minutes"
                  showRequired={false}
                />

               
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* SLOT DURATION */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Slot Duration
              </h3>
              <div className="flex flex-col">
                
                <InputField
                  label="Duration (minutes)"
                  name="slotDuration"
                  value={formData.slotDuration}
                  onChange={handleChange}
                  error={fieldErrors.slotDuration}
                  type="number"
                  min="1"
                  placeholder="Enter duration in minutes"
                  showRequired={false}
                />

               
              </div>
            </div> */}

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* ABOUT DOCTOR */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                About Doctor
              </h3>
              <InputField
                label="Doctor Bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                type="textarea"
                rows={4}
                error={fieldErrors.bio}
              />
            </div>
          </div>

          {/* ────────────────────────────────────────────────────────────────────── */}
          {/* SUBMIT BUTTON */}
          {/* ────────────────────────────────────────────────────────────────────── */}
          <div className="flex justify-center mt-10">
            <button
              onClick={handleNext}
              // disabled={!isFormValid() || isLoggingIn}
              disabled={isLoggingIn}
              className="w-full max-w-md rounded-full bg-gradient-to-r from-[#004172] to-[#3cba84] px-10 py-3 font-medium text-white disabled:opacity-50 transition-opacity md:w-[335px] md:max-w-none"
            >
              {isLoggingIn ? "Loading..." : "Next"}
            </button>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  rows = 1,
  showRequired = true,
  error,
  ...rest
}) {
  const isTextarea = type === "textarea";
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} {showRequired && <span className="text-red-500">*</span>}
      </label>
      {isTextarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={rows}
          placeholder="Enter here"
          required={showRequired}
          className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition resize-none ${
            error ? 'border-red-500' : 'border-gray-200'
          }`}
          {...rest}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder="Enter here"
          required={showRequired}
          className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
            error ? 'border-red-500' : 'border-gray-200'
          }`}
          {...rest}
        />
      )}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function FileInputField({ label, name, file, onChange, error, onDelete }) {
  const [previewURL, setPreviewURL] = useState(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewURL(url);
      return () => {
        URL.revokeObjectURL(url);
        setPreviewURL(null);
      };
    } else {
      setPreviewURL(null);
    }
  }, [file]);

  const isImage = file && file.type.startsWith("image/");

  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label}
      </label>
      <input
        type="file"
        name={name}
        accept="image/*,application/pdf"
        onChange={onChange}
        className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
          error ? 'border-red-500' : 'border-gray-200'
        }`}
      />

      {file && (
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-600">Selected: {file.name}</p>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="text-red-500 hover:text-red-700 ml-2 p-1 rounded-full hover:bg-red-50 transition"
              title="Delete file"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {isImage && previewURL && (
        <img
          src={previewURL}
          alt="Preview"
          className="mt-2 max-w-[200px] max-h-[150px] object-contain border border-gray-200 rounded-md shadow-sm"
        />
      )}

      {/* Only render PDF link if file exists AND is a PDF */}
      {file && !isImage && previewURL && file.type === "application/pdf" && (
        <p className="mt-2">
          <a
            href={previewURL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#004276] underline text-sm hover:text-[#003a5c] transition"
          >
            View PDF
          </a>
        </p>
      )}
      
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function DatePickerField({ label, name, value, onChange, error }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label}
      </label>
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={(date) => {
          onChange({
            target: {
              name,
              value: date ? date.format("YYYY-MM-DD") : "",
            },
          });
        }}
        format="DD-MM-YYYY"
        placeholder="DD-MM-YYYY"
        className={`w-full border border-gray-200 rounded-lg px-3 py-2 ${
          error ? 'border-red-500' : 'border-gray-200'
        }`}
        showToday
        allowClear
      />
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function SelectField({ label, name, value, onChange, options }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} <span className="text-red-500">*</span>
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
      >
        <option value="">Select</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

export default DoctorOnboarding;